/**
 * Hook that owns the Well Test Overview Columns editable table's local row
 * state, validation (Save-only), and serialization to
 * organization.options.well_test_overview_options.
 *
 * Persisted shape verified against the real consumer,
 * backbone/controllers/stops/stop_well_test_helper.js (filterValidAttributes /
 * filterUsedValues) — not WI 40366's own JSON sample, which never exercises
 * the optional decimal_places_shown key.
 */
import { useCallback, useRef, useState } from 'react';
// Digits only, comma-separated, at most one space after each comma (not
// zero-or-more), no leading/trailing/double commas.
const ATTRIBUTE_CODE_LIST_PATTERN = /^\d+(, ?\d+)*$/;
export function parseAttributeTypeCodes(text) {
    const trimmed = text.trim();
    if (trimmed === '' || !ATTRIBUTE_CODE_LIST_PATTERN.test(trimmed)) {
        return null;
    }
    return trimmed.split(',').map((part) => parseInt(part.trim(), 10));
}
export function parseDecimalPlaces(text) {
    const trimmed = text.trim();
    if (trimmed === '') {
        return { valid: true, value: undefined };
    }
    if (!/^\d+$/.test(trimmed)) {
        return { valid: false };
    }
    const value = parseInt(trimmed, 10);
    return value >= 0 && value <= 10 ? { valid: true, value } : { valid: false };
}
function storedOption(org) {
    var _a;
    const stored = (_a = org === null || org === void 0 ? void 0 : org.options) === null || _a === void 0 ? void 0 : _a.well_test_overview_options;
    return typeof stored === 'object' && stored !== null ? stored : undefined;
}
function toRow(column) {
    return {
        id: crypto.randomUUID(),
        name: column.name,
        attributeTypeCodeText: (column.attribute_type_code || []).join(', '),
        decimalPlacesText: typeof column.decimal_places_shown === 'number' ? String(column.decimal_places_shown) : '',
    };
}
function toStoredColumn(row) {
    var _a;
    const decimalPlaces = parseDecimalPlaces(row.decimalPlacesText);
    const column = {
        name: row.name.trim(),
        attribute_type_code: (_a = parseAttributeTypeCodes(row.attributeTypeCodeText)) !== null && _a !== void 0 ? _a : [],
    };
    if (decimalPlaces.valid && decimalPlaces.value !== undefined) {
        column.decimal_places_shown = decimalPlaces.value;
    }
    return column;
}
export function useWellTestOverviewColumns(org) {
    const initialStoredOption = storedOption(org);
    const hadStoredOptionRef = useRef(initialStoredOption != null);
    const [rows, setRows] = useState(() => { var _a; return ((_a = initialStoredOption === null || initialStoredOption === void 0 ? void 0 : initialStoredOption.columns) !== null && _a !== void 0 ? _a : []).map(toRow); });
    const [errors, setErrors] = useState({});
    const reset = useCallback(() => {
        var _a;
        const stored = storedOption(org);
        hadStoredOptionRef.current = stored != null;
        setRows(((_a = stored === null || stored === void 0 ? void 0 : stored.columns) !== null && _a !== void 0 ? _a : []).map(toRow));
        setErrors({});
    }, [org]);
    const addRow = useCallback(() => {
        setRows((prev) => [...prev, { id: crypto.randomUUID(), name: '', attributeTypeCodeText: '', decimalPlacesText: '' }]);
    }, []);
    const removeRow = useCallback((id) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    }, []);
    const updateRow = useCallback((id, field, value) => {
        setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    }, []);
    const moveRow = useCallback((fromIndex, toIndex) => {
        setRows((previous) => {
            if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= previous.length || toIndex >= previous.length) {
                return previous;
            }
            const next = [...previous];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);
    const validate = useCallback(() => {
        const next = {};
        for (const row of rows) {
            const rowErrors = {};
            if (!row.name.trim()) {
                rowErrors.name = 'Required';
            }
            if (!row.attributeTypeCodeText.trim()) {
                rowErrors.attributeTypeCode = 'Required';
            }
            else if (parseAttributeTypeCodes(row.attributeTypeCodeText) === null) {
                rowErrors.attributeTypeCode = 'Enter digits separated by commas (e.g. 78,206 or 79, 201).';
            }
            if (!parseDecimalPlaces(row.decimalPlacesText).valid) {
                rowErrors.decimalPlaces = 'Must be a whole number between 0 and 10.';
            }
            if (Object.keys(rowErrors).length > 0) {
                next[row.id] = rowErrors;
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    }, [rows]);
    const toOptionsPatch = useCallback(() => {
        if (rows.length === 0 && !hadStoredOptionRef.current) {
            // Never configured, never touched — don't introduce the key on an
            // unrelated save (would silently override the grid's 4-default fallback).
            return {};
        }
        return { well_test_overview_options: { columns: rows.map(toStoredColumn) } };
    }, [rows]);
    return { rows, errors, addRow, removeRow, updateRow, moveRow, validate, reset, toOptionsPatch };
}
